import React, { useState, useEffect } from 'react';
import {
  Avatar, Box, Button, Card, CardContent, Chip, Container, Divider,
  Grid, Paper, TextField, Typography, IconButton, Tab, Tabs,
  List, ListItem, ListItemText, ListItemSecondaryAction, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';
import { supabase } from '../supabase/config';

const Profile = () => {
  const { user } = useSupabaseAuth();
  const [loadingData, setLoadingData] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    preferences: {
      dietary: [],
      allergies: [],
      favorite_ingredients: []
    }
  });
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [mealHistory, setMealHistory] = useState([]);
  const navigate = useNavigate();

  // Update fetchUserData to use Supabase
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      try {
        setLoadingData(true);
        
        // Get user profile data from Supabase
        const { data: userData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile:', profileError);
          throw profileError;
        }
        
        if (userData) {
          // Handle potential missing properties with default values
          setProfileData({
            name: user.user_metadata?.name || userData.name || '',
            email: user.email || userData.email || '',
            preferences: userData.preferences || {
              dietary: [],
              allergies: [],
              favorite_ingredients: []
            }
          });
        } else {
          // Create user profile if it doesn't exist
          const newProfile = {
            id: user.id,
            name: user.user_metadata?.name || '',
            email: user.email || '',
            preferences: {
              dietary: [],
              allergies: [],
              favorite_ingredients: []
            }
          };
          
          const { error: createError } = await supabase
            .from('profiles')
            .insert([newProfile]);
            
          if (createError) {
            console.error('Error creating profile:', createError);
            throw createError;
          }
          
          setProfileData(newProfile);
        }
        
        // Fetch saved recipes
        const { data: recipes = [], error: recipesError } = await supabase
          .from('recipes')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (recipesError) {
          console.error('Error fetching recipes:', recipesError);
          // Still set empty array to avoid undefined
          setSavedRecipes([]);
        } else {
          setSavedRecipes(recipes || []);
        }
        
        // Fetch meal plans
        const { data: plans = [], error: plansError } = await supabase
          .from('meal_plans')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (plansError) {
          console.error('Error fetching meal plans:', plansError);
          // Still set empty array to avoid undefined
          setMealHistory([]);
        } else {
          setMealHistory(plans || []);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load profile data. Please try again later.');
        
        // Initialize with safe default values to prevent runtime errors
        setSavedRecipes([]);
        setMealHistory([]);
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    
    try {
      // Update user metadata in Supabase Auth
      const { error: userUpdateError } = await supabase.auth.updateUser({
        data: { name: profileData.name }
      });
      
      if (userUpdateError) throw userUpdateError;
      
      // Update user profile in database
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({
          name: profileData.name,
          preferences: profileData.preferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (profileUpdateError) throw profileUpdateError;
      
      setEditMode(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    }
  };

  const handlePreferenceChange = (preferenceType, value) => {
    setProfileData((prev) => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [preferenceType]: value
      }
    }));
  };

  const handleAddPreference = (preferenceType, value) => {
    if (!value.trim()) return;
    
    const currentPreferences = profileData.preferences[preferenceType] || [];
    
    if (!currentPreferences.includes(value)) {
      handlePreferenceChange(
        preferenceType, 
        [...currentPreferences, value]
      );
    }
  };

  const handleRemovePreference = (preferenceType, index) => {
    const currentPreferences = profileData.preferences[preferenceType] || [];
    handlePreferenceChange(
      preferenceType,
      currentPreferences.filter((_, i) => i !== index)
    );
  };

  const handleDeleteRecipe = async (recipeId) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('recipes')
        .delete()
        .eq('id', recipeId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Update the saved recipes list
      setSavedRecipes((prev) => prev.filter(recipe => recipe.id !== recipeId));
      toast.success('Recipe deleted successfully');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Failed to delete recipe');
    }
  };

  const handleViewRecipe = (recipeId) => {
    // Navigate to the recipe detail page
    navigate(`/recipe/${recipeId}`);
  };

  if (loadingData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center', py: 8 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading profile...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
            My Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your personal information, preferences, and saved content
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PersonIcon />} label="Personal Info" />
          <Tab 
            icon={<BookmarkIcon />} 
            label={`Saved Recipes (${savedRecipes ? savedRecipes.length : 0})`} 
          />
          <Tab 
            icon={<HistoryIcon />} 
            label={`Meal History (${mealHistory ? mealHistory.length : 0})`} 
          />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    Personal Information
                  </Typography>
                  {!editMode && (
                    <Button 
                      startIcon={<EditIcon />} 
                      onClick={() => setEditMode(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Name"
                      fullWidth
                      value={profileData.name || ''}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email"
                      fullWidth
                      value={profileData.email || ''}
                      disabled
                    />
                  </Grid>
                </Grid>

                {editMode && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => setEditMode(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleSaveProfile}
                    >
                      Save Changes
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
            
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    Dietary Preferences
                  </Typography>
                  {!editMode && (
                    <Button 
                      startIcon={<EditIcon />} 
                      onClick={() => setEditMode(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>
                      Dietary Restrictions
                    </Typography>
                    
                    {editMode ? (
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          label="Add Dietary Restriction"
                          size="small"
                          fullWidth
                          placeholder="e.g. Vegetarian"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddPreference('dietary', e.target.value);
                              e.target.value = '';
                            }
                          }}
                        />
                      </Box>
                    ) : null}
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profileData.preferences?.dietary?.length ? (
                        profileData.preferences.dietary.map((item, index) => (
                          <Chip 
                            key={index} 
                            label={item} 
                            onDelete={editMode ? () => handleRemovePreference('dietary', index) : undefined}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No dietary restrictions added
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>
                      Allergies
                    </Typography>
                    
                    {editMode ? (
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          label="Add Allergy"
                          size="small"
                          fullWidth
                          placeholder="e.g. Peanuts"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddPreference('allergies', e.target.value);
                              e.target.value = '';
                            }
                          }}
                        />
                      </Box>
                    ) : null}
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profileData.preferences?.allergies?.length ? (
                        profileData.preferences.allergies.map((item, index) => (
                          <Chip 
                            key={index} 
                            label={item} 
                            onDelete={editMode ? () => handleRemovePreference('allergies', index) : undefined}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No allergies added
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" gutterBottom>
                      Favorite Ingredients
                    </Typography>
                    
                    {editMode ? (
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          label="Add Favorite Ingredient"
                          size="small"
                          fullWidth
                          placeholder="e.g. Avocado"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddPreference('favorite_ingredients', e.target.value);
                              e.target.value = '';
                            }
                          }}
                        />
                      </Box>
                    ) : null}
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profileData.preferences?.favorite_ingredients?.length ? (
                        profileData.preferences.favorite_ingredients.map((item, index) => (
                          <Chip 
                            key={index} 
                            label={item} 
                            onDelete={editMode ? () => handleRemovePreference('favorite_ingredients', index) : undefined}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No favorite ingredients added
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
        
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Saved Recipes
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {savedRecipes && savedRecipes.length > 0 ? (
              <List>
                {savedRecipes.map((recipe) => (
                  <ListItem 
                    key={recipe.id} 
                    button 
                    onClick={() => handleViewRecipe(recipe.id)}
                    sx={{ 
                      mb: 1, 
                      borderRadius: 1, 
                      border: '1px solid', 
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={recipe.title || 'Untitled Recipe'} 
                      secondary={recipe.cuisine || recipe.meal_type || 'No description'}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRecipe(recipe.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  You don't have any saved recipes yet
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/recipe-generator')}
                  sx={{ mt: 2 }}
                  startIcon={<AddIcon />}
                >
                  Create a Recipe
                </Button>
              </Box>
            )}
          </Box>
        )}
        
        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Meal Plan History
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {mealHistory && mealHistory.length > 0 ? (
              <List>
                {mealHistory.map((plan) => (
                  <ListItem 
                    key={plan.id} 
                    button 
                    sx={{ 
                      mb: 1, 
                      borderRadius: 1, 
                      border: '1px solid', 
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={`${plan.days}-Day Meal Plan`} 
                      secondary={`${plan.dietary || 'Standard'} Diet • ${plan.calories_per_day || 2000} calories/day`}
                    />
                    <Chip 
                      label={plan.is_saved ? "Saved" : "Generated"} 
                      size="small" 
                      color={plan.is_saved ? "primary" : "default"}
                      sx={{ mr: 1 }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  You don't have any meal plans yet
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/meal-planner')}
                  sx={{ mt: 2 }}
                  startIcon={<AddIcon />}
                >
                  Create a Meal Plan
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Profile;
